<template>
    <Paper :header="$t('spotlight.languages.header')" tall>
        <LanguagesSkeleton v-if="isLoading" />
        <div v-else class="p-4 grid-test">
            <h6 class="subtitle bold mb-0">
                {{ $t('spotlight.languages.languageLabel') }}
            </h6>
            <h6 class="subtitle bold mb-0">
                {{ $t('spotlight.languages.levelLabel') }}
            </h6>
            <template v-for="(item, index) in savedLanguages">
                <Divider
                    :key="'div' + index + item.language.label + item.proficiency.label"
                    class="span-width"
                />
                <BaseSearchable
                    :key="'lang' + index + item.language.label + item.proficiency.label"
                    :data="filteredLanguages"
                    :value="item.language"
                    :stateEnum="item.language.enumKey"
                    @input="emitLanguageChange(index, 'language', ...arguments)"
                />
                <BaseSearchable
                    :key="'prof' + index + item.language.label + item.proficiency.label"
                    :data="proficiencies"
                    :value="item.proficiency"
                    :stateEnum="item.proficiency.enumKey"
                    @input="emitLanguageChange(index, 'proficiency', ...arguments)"
                />
                <BaseIcon
                    :key="'del' + index"
                    name="trash"
                    class="clickable"
                    @click.native="removeLanguage(index)"
                />
            </template>
            <div></div>
            <BaseButton
                class="span-width"
                variant="outline-secondary"
                disabledHint="Please finish input"
                :disabled="shouldFinishInput"
                @click="addNewLanguage"
                >{{ $t('spotlight.languages.addLanguage') }}</BaseButton
            >
        </div>
    </Paper>
</template>

<script>
import StateEnum from '~/utils/StateEnum';

import languages from 'highered-utils/data/languages.json';
import proficiencies from 'highered-utils/data/languageProficiencies.json';

// Components
import LanguagesSkeleton from '../skeletons/LanguagesSkeleton';

export default {
    name: 'Languages',
    components: { LanguagesSkeleton },
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        oldLanguage: {
            type: Array,
            default: () => [],
        },
        isLoading: Boolean,
    },
    data: () => ({
        languages: languages,
    }),
    computed: {
        // Filters the languages already selected
        filteredLanguages() {
            return this.languages.filter(
                ({ value: langValue }) =>
                    !this.savedLanguages.find(({ language: { value } }) => value === langValue)
            );
        },
        // Converts languages to the form of {value, label} (BaseSearchable requirement)
        savedLanguages() {
            const languages = Object.assign([], this.value);
            const oldLanguages = Object.assign([], this.oldLanguage);
            return languages.map((lang, index) => {
                const oldLang = oldLanguages[index] || {};
                const stateEnum = this.compareAndGetEnumKey(lang.languageId, oldLang.languageId);
                const stateLevelEnum = this.compareAndGetEnumKey(
                    lang.proficiencyId,
                    oldLang.proficiencyId
                );

                const langProfKey = this.$t(
                    `spotlight.languages.proficiencies.${lang.proficiencyId}`
                ); // i18n key
                return {
                    language: {
                        label: lang.language,
                        value: lang.languageId,
                        enumKey: stateEnum,
                    },
                    proficiency: {
                        label: this.$te(langProfKey) ? this.$t(langProfKey) : lang.proficiency,
                        value: lang.proficiencyId,
                        enumKey: stateLevelEnum,
                    },
                };
            });
        },
        shouldFinishInput() {
            if (this.savedLanguages.length === 0) {
                return false;
            }

            const { language, proficiency } = this.savedLanguages[this.savedLanguages.length - 1];
            return (
                this.savedLanguages.length > 0 &&
                (language.value === '' ||
                    language.value === undefined ||
                    (proficiency.value === '' || proficiency.value === undefined))
            );
        },
        proficiencies() {
            proficiencies.forEach(p => {
                const langProfKey = `spotlight.languages.proficiencies.${p.value}`; // i18n key
                p.label = this.$te(langProfKey) ? this.$t(langProfKey) : p.label;
            });
            return proficiencies;
        },
    },
    methods: {
        onValidate() {
            return true;
        },
        // Returns a state-enum based on two strings are equal or not
        compareAndGetEnumKey(string1, string2) {
            if (string1 === string2) {
                return string1 === '' ? undefined : StateEnum.SUCCESS;
            } else {
                return StateEnum.UNSAVED;
            }
        },
        // Adds a new empty language language
        addNewLanguage() {
            let newLanguages = Object.assign([], this.value);
            newLanguages.push({
                language: '',
                languageId: '',
                proficiency: '',
                proficiencyId: '',
            });
            this.$emit('input', newLanguages);
        },
        // Removes a language by index
        removeLanguage(index) {
            const newLanguages = Object.assign([], this.value);
            if (newLanguages.length > 1) {
                newLanguages.splice(index, 1);
                this.$emit('input', newLanguages);
            } else {
                this.$emit('input', []);
            }
        },
        // Updates an existing language
        emitLanguageChange(index, type, { value, label }) {
            const newLanguages = Object.assign([], this.value);
            if (newLanguages.length > index) {
                newLanguages[index][type] = label;
                newLanguages[index][type + 'Id'] = value;
            }
            this.$emit('input', newLanguages);
        },
        // Finds an existing language
        findOldLanguage(index, langValue, valueName) {
            const oldLangs = Object.assign([], this.oldLanguage);
            if (oldLangs.length >= index) {
                return {};
            }
            return oldLangs[index][valueName] === langValue ? oldLangs[index] : {};
        },
    },
};
</script>

<style scoped>
.grid-test {
    display: grid;
    grid-auto-columns: auto auto 20px;
    grid-auto-rows: auto auto;
    grid-auto-flow: row;
    grid-gap: 10px;
}

.span-width {
    grid-column: span 3;
}

.align-center {
    align-content: center !important;
}

.align-center > span {
    align-self: center;
}
</style>
